// apps/flux-ems-web/src/components/simulation/AgentNetworkDiagram.jsx
import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';

const GOLD = '#b28c50';
const GOLD_DIM = 'rgba(178, 140, 80, 0.3)';
const HEADING_FONT = "'Syne', sans-serif";
const MONO_FONT = "'JetBrains Mono', monospace";

/**
 * Agent Network Diagram — shows agent nodes and coordination connections.
 *
 * @param {Object} props
 * @param {string} props.phase - 'initializing' | 'siloed' | 'orchestrated' | 'complete' | 'playback'
 * @param {Object} props.agents - { battery: bool, ev_fleet: bool, cold_storage: bool }
 * @param {number} props.coordinationIntensity - 0-1, how active coordination is
 * @param {Object} props.signals - { battery: string, ev_fleet: string, cold_storage: string } (signal labels)
 * @param {boolean} props.compact - Smaller size for sidebar usage
 */
export default function AgentNetworkDiagram({
    phase = 'initializing',
    agents = {},
    coordinationIntensity = 0,
    signals = {},
    compact = false,
}) {
    const canvasRef = useRef(null);
    const animRef = useRef(null);

    const size = compact ? 220 : 300;
    const cx = size / 2;
    const cy = size / 2;
    const radius = compact ? 70 : 100;

    // Agent positions (triangle arrangement)
    const agentNodes = [
        { id: 'battery', label: agents.site_dna ? 'Solar+Batt' : 'Battery', angle: -90, configured: !!(agents.battery || agents.site_dna) },
        { id: 'ev_fleet', label: 'EV Fleet', angle: 30, configured: !!agents.ev_fleet },
        { id: 'cold_storage', label: 'Cold', angle: 150, configured: !!agents.cold_storage },
    ];

    const getPos = (angle) => ({
        x: cx + radius * Math.cos((angle * Math.PI) / 180),
        y: cy + radius * Math.sin((angle * Math.PI) / 180),
    });

    const isActive = phase === 'orchestrated' || phase === 'complete' || phase === 'playback';
    const showConnections = isActive && coordinationIntensity > 0;

    // Pulse animation
    useEffect(() => {
        if (!canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let frame = 0;
        const animate = () => {
            frame++;
            ctx.clearRect(0, 0, size, size);

            // Draw connections from center to each agent
            if (showConnections) {
                agentNodes.forEach((node) => {
                    if (!node.configured) return;
                    const pos = getPos(node.angle);
                    const pulsePhase = (frame * 0.03 + node.angle * 0.01) % 1;
                    const alpha = 0.15 + coordinationIntensity * 0.6 *
                        (0.5 + 0.5 * Math.sin(pulsePhase * Math.PI * 2));
                    const width = 1 + coordinationIntensity * 3;

                    ctx.beginPath();
                    ctx.moveTo(cx, cy);
                    ctx.lineTo(pos.x, pos.y);
                    ctx.strokeStyle = `rgba(178, 140, 80, ${alpha})`;
                    ctx.lineWidth = width;
                    ctx.stroke();

                    // Traveling pulse dot
                    if (coordinationIntensity > 0.3) {
                        const t = pulsePhase;
                        const dotX = cx + (pos.x - cx) * t;
                        const dotY = cy + (pos.y - cy) * t;
                        ctx.beginPath();
                        ctx.arc(dotX, dotY, 3, 0, Math.PI * 2);
                        ctx.fillStyle = `rgba(178, 140, 80, ${alpha + 0.2})`;
                        ctx.fill();
                    }
                });
            }

            // Center node (Vortx)
            const centerAlpha = isActive ? 0.8 + 0.2 * Math.sin(frame * 0.05) : 0.2;
            ctx.beginPath();
            ctx.arc(cx, cy, compact ? 16 : 22, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(178, 140, 80, ${centerAlpha * 0.3})`;
            ctx.fill();
            ctx.strokeStyle = `rgba(178, 140, 80, ${centerAlpha})`;
            ctx.lineWidth = 2;
            ctx.stroke();

            // Agent node halos
            agentNodes.forEach((node) => {
                if (!node.configured) return;
                const pos = getPos(node.angle);
                const nodeAlpha = phase === 'siloed'
                    ? 0.3 + 0.3 * Math.sin(frame * 0.04 + node.angle)
                    : isActive ? 0.6 + coordinationIntensity * 0.3 : 0.15;
                ctx.beginPath();
                ctx.arc(pos.x, pos.y, compact ? 20 : 26, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(178, 140, 80, ${nodeAlpha * 0.15})`;
                ctx.fill();
                ctx.strokeStyle = `rgba(178, 140, 80, ${nodeAlpha})`;
                ctx.lineWidth = 1.5;
                ctx.stroke();
            });

            animRef.current = requestAnimationFrame(animate);
        };

        animate();
        return () => {
            if (animRef.current) cancelAnimationFrame(animRef.current);
        };
    }, [phase, coordinationIntensity, showConnections, size]);

    const getPhaseLabel = () => {
        if (phase === 'initializing') return 'Detecting agents...';
        if (phase === 'siloed') return 'Independent operation';
        if (phase === 'orchestrated') return 'Coordinating';
        if (phase === 'complete') return 'Coordination complete';
        if (phase === 'playback') {
            return coordinationIntensity > 0.3 ? 'Active coordination' : 'Low coordination';
        }
        return '';
    };

    return (
        <Box sx={{ position: 'relative', width: size, height: size + (compact ? 40 : 80) }}>
            <canvas
                ref={canvasRef}
                width={size}
                height={size}
                style={{ position: 'absolute', top: 0, left: 0 }}
            />

            {/* Center label */}
            <Typography
                sx={{
                    position: 'absolute',
                    top: cy - 8,
                    left: cx - 20,
                    width: 40,
                    textAlign: 'center',
                    color: isActive ? GOLD : 'rgba(178,140,80,0.4)',
                    fontFamily: HEADING_FONT,
                    fontSize: compact ? '0.6rem' : '0.7rem',
                    fontWeight: 700,
                }}
            >
                Vortx
            </Typography>

            {/* Agent labels */}
            {agentNodes.map((node) => {
                const pos = getPos(node.angle);
                const signal = signals[node.id];
                return (
                    <Box
                        key={node.id}
                        sx={{
                            position: 'absolute',
                            top: pos.y + (compact ? 22 : 28),
                            left: pos.x - (compact ? 40 : 50),
                            width: compact ? 80 : 100,
                            textAlign: 'center',
                            opacity: node.configured ? 1 : 0.3,
                        }}
                    >
                        <Typography
                            sx={{
                                color: node.configured ? GOLD : GOLD_DIM,
                                fontFamily: MONO_FONT,
                                fontSize: compact ? '0.65rem' : '0.75rem',
                                fontWeight: 600,
                            }}
                        >
                            {node.label}
                        </Typography>
                        {signal && (
                            <Typography
                                sx={{
                                    color: 'rgba(178,140,80,0.7)',
                                    fontFamily: MONO_FONT,
                                    fontSize: '0.6rem',
                                    mt: 0.25,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {signal}
                            </Typography>
                        )}
                    </Box>
                );
            })}

            {/* Phase label */}
            <Typography
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    color: 'rgba(178,140,80,0.6)',
                    fontFamily: MONO_FONT,
                    fontSize: '0.65rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                }}
            >
                {getPhaseLabel()}
            </Typography>
        </Box>
    );
}
